/**
 * Synthetic data generators for degraded mode
 * Shared between api.ts and api-client.ts
 */

export function synthLabResults(count: number): any[] {
  const tests = [
    { loinc: "2345-7", name: "Glucose" }, { loinc: "2160-0", name: "Creatinine" },
    { loinc: "1742-6", name: "ALT (SGPT)" }, { loinc: "1920-8", name: "AST (SGOT)" },
    { loinc: "718-7", name: "Hemoglobine" }, { loinc: "789-8", name: "Num. erythrocytes" },
    { loinc: "4544-3", name: "Hematocrite" }, { loinc: "787-2", name: "MCV" },
    { loinc: "785-6", name: "MCH" }, { loinc: "786-4", name: "MCHC" },
    { loinc: "777-3", name: "Plaquettes" }, { loinc: "6690-2", name: "Num. leucocytes" },
    { loinc: "3024-7", name: "Sodium" }, { loinc: "2823-3", name: "Potassium" },
    { loinc: "1751-7", name: "Albumine" }, { loinc: "1751-7", name: "Proteines totales" },
    { loinc: "1743-4", name: "ALP" }, { loinc: "2324-2", name: "GGT" },
    { loinc: "2532-0", name: "LDH" }, { loinc: "2093-3", name: "Cholesterol total" },
    { loinc: "13457-7", name: "Cholesterol HDL" }, { loinc: "13458-5", name: "Cholesterol LDL" },
    { loinc: "2571-8", name: "Triglycerides" }, { loinc: "14959-1", name: "Microalbumine" },
    { loinc: "10839-9", name: "Troponine I" }, { loinc: "33747-0", name: "PSA total" },
  ];
  const analyzers = ["Roche Cobas c702", "Sysmex XN-1000", "Mindray BC-6800", "Siemens Atellica", "Abbott Architect ci8200", "bioMerieux VITEK 2"];
  const statuses = ["validated", "pending", "amended"];
  const flags = ["N", "H", "L", null];
  return Array.from({ length: count }, (_, i) => {
    const t = tests[i % tests.length];
    const v = Math.round((Math.random() * 200 - 20) * 100) / 100;
    const flag = flags[Math.floor(Math.random() * flags.length)];
    return {
      id: i + 1,
      patient_id: Math.floor(Math.random() * 104) + 1,
      loinc_code: t.loinc,
      test_name: t.name,
      value: v,
      unit: t.name.includes("Hemoglobine") ? "g/dL" : t.name.includes("Glucose") || t.name.includes("Cholesterol") ? "mg/dL" : t.name.includes("Sodium") || t.name.includes("Potassium") ? "mmol/L" : t.name.includes("num") || t.name.includes("Num") ? "10^9/L" : "mg/L",
      reference_low: 0,
      reference_high: 100,
      abnormal_flag: flag,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      resulted_at: new Date(Date.now() - Math.random() * 86400000 * 7).toISOString(),
      analyzer: analyzers[i % analyzers.length],
    };
  });
}

export function synthPatients(count: number): any[] {
  const names = ["Alami", "Benali", "Chraibi", "Daoud", "El Fassi", "Ghali", "Hassani", "Idrissi", "Jabri", "Kettani", "Lahlou", "Mansouri", "Naciri", "Ouazzani", "Rifi"];
  const firstNames = ["Mohamed", "Fatima", "Ahmed", "Aicha", "Hassan", "Khadija", "Youssef", "Zineb"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    ins: `1${String(1000000000 + i + 1).padStart(10, "0")}`,
    first_name: firstNames[i % firstNames.length],
    last_name: names[i % names.length],
    birth_date: new Date(1960 + (i % 50), i % 12, (i % 28) + 1).toISOString().split("T")[0],
    gender: i % 2 === 0 ? "M" : "F",
    blood_type: ["A+", "O+", "B+", "AB+", "O-", "A-"][i % 6],
  }));
}

export function synthEncounters(count: number): any[] {
  const types = ["consultation", "urgence", "teleconsultation", "controle"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    patient_id: Math.floor(Math.random() * 104) + 1,
    doctor_id: Math.floor(Math.random() * 8) + 1,
    type: types[i % types.length],
    status: ["completed", "in_progress", "cancelled"][i % 3],
    reason: ["Fievre", "Douleur thoracique", "Suivi diabete", "Bilan annuel", "Consultation specialiste"][i % 5],
    started_at: new Date(Date.now() - Math.random() * 86400000 * 30).toISOString(),
  }));
}

export function synthPharmacyStock(count: number): any[] {
  const meds = ["Paracetamol", "Amoxicilline", "Ibuprofene", "Metformine", "Omeprazole", "Atorvastatine", "Amlodipine", "Ramipril", "Salbutamol", "Insuline"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    medication_id: `MED-${String(i + 1).padStart(3, "0")}`,
    medication_name: meds[i % meds.length],
    dci: meds[i % meds.length],
    atc_code: ["N02BE01", "J01CA04", "M01AE01", "A10BA02", "A02BC01", "C10AA05", "C08CA01", "C09AA05", "R03AC02", "A10AB01"][i % 10],
    form: ["comprime", "gelule", "sirop", "injectable"][i % 4],
    dosage: ["500mg", "1g", "200mg", "850mg", "20mg"][i % 5],
    quantity: Math.floor(Math.random() * 200),
    min_stock: 20,
    max_stock: 500,
    expiry_date: new Date(Date.now() + (30 + Math.random() * 365) * 86400000).toISOString().split("T")[0],
    controlled: i % 20 === 0,
  }));
}

export function synthBillingClaims(count: number): any[] {
  const statuses = ["paid", "pending", "submitted", "rejected"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    claim_number: `CLM-2026-${String(i + 1).padStart(5, "0")}`,
    patient_id: Math.floor(Math.random() * 104) + 1,
    encounter_id: Math.floor(Math.random() * 312) + 1,
    ngap_codes: [{ code: "C", letter: "C", base_amount: 75, modifiers: [] }],
    total_mad: 75 + Math.floor(Math.random() * 500),
    cnss_rate: [70, 100][i % 2],
    reimbursed_mad: (75 + Math.floor(Math.random() * 500)) * 0.7,
    status: statuses[i % statuses.length],
    submitted_at: new Date(Date.now() - Math.random() * 86400000 * 30).toISOString(),
  }));
}

export function synthImagingStudies(count: number): any[] {
  const modalities = ["CT", "MR", "US", "MG", "XR"];
  const bodyParts = ["Thorax", "Abdomen", "Crane", "Genou", "Epaule", "Bassin", "Rachis"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    patient_id: Math.floor(Math.random() * 104) + 1,
    modality: modalities[i % modalities.length],
    study_uid: `1.2.840.0.${Date.now()}.${i}`,
    body_part: bodyParts[i % bodyParts.length],
    indication: ["Douleur", "Trauma", "Bilan", "Suivi"][i % 4],
    status: ["completed", "in_progress", "reported", "scheduled"][i % 4],
    performed_at: new Date(Date.now() - Math.random() * 86400000 * 14).toISOString(),
    radiologist_id: Math.floor(Math.random() * 2) + 1,
  }));
}

export function synthAlerts(count: number): any[] {
  const severities = ["critical", "warning", "info"];
  const titles = ["Kaliemie elevee", "Troponine positive", "Septic suspectee", "Glucose critique", "Hemoglobine basse", "Arret cardiaque"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    patient_id: Math.floor(Math.random() * 104) + 1,
    source: ["lab", "pharmacy", "clinical", "system"][i % 4],
    severity: severities[i % severities.length],
    title: titles[i % titles.length],
    body: "Alerte automatique - action requise",
    acknowledged: i % 3 === 0,
    created_at: new Date(Date.now() - Math.random() * 86400000).toISOString(),
  }));
}